import { useEffect, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  Shield,
  Store,
  User,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Lock,
  Mail,
  Zap,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
  loginChallenge,
  loginFailure,
  loginRequest,
  loginSuccess,
} from "@/store/slices/authSlice";
import { makeRequest } from "@/utils/makeRequest";
import { getErrorMessage } from "@/utils/getErrorMessage";
import { UserRole } from "@/interfaces/profile";
import { fetchSettings } from "@/store/actions/settingsActions";
import { Logo } from "@/components/Logo";

interface LoginOption {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  bgGradient: string;
  path: string;
}

// Google Icon Component
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="currentColor"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="currentColor"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="currentColor"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="currentColor"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

// Apple Icon Component
const AppleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.74s2.57-1.04 4.15-.88c.7.03 2.67.26 3.93 2.1-.07.04-2.35 1.37-2.33 4.08.02 3.26 2.85 4.34 2.88 4.35-.02.1-.45 1.52-1.49 3.06-.9 1.3-1.83 2.6-3.22 2.52zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
  </svg>
);

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [loginMethod, setLoginMethod] = useState<"role" | "email" | null>(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState<
    "google" | "apple" | null
  >(null);
  const [rememberMe, setRememberMe] = useState(false);

  const location = useLocation();

  const { user, isLoading } = useAppSelector((state) => state.auth);

  useEffect(() => {
    if (!user || isLoading) {
      return;
    }

    const roleRoute =
      user.role === UserRole.SUPER || user.role === UserRole.ADMIN
        ? "/admin"
        : "/dashboard";

    const target =
      location.state?.from?.pathname ||
      sessionStorage.getItem("redirectAfterLogin") ||
      roleRoute;

    sessionStorage.removeItem("redirectAfterLogin");

    navigate(target, {
      replace: true,
    });
  }, [user, isLoading, navigate, location.state]);

  useEffect(() => {
    return () => {
      dispatch(loginChallenge());
    };
  }, []);

  const handleEmailLoginSelect = () => {
    setLoginMethod("email");
  };

  const handleSocialLogin = async (provider: "google" | "apple") => {
    setIsSocialLoading(provider);

    // toast({
    //   title: `${provider === "google" ? "Google" : "Apple"} Login Successful`,
    //   description: "Welcome to BUYFIAT!",
    // });

    setIsSocialLoading(null);
    // navigate("/dashboard");
  };

  const handleRegularLogin = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!email || !password) return;

    dispatch(loginRequest());
    try {
      const res = await makeRequest.post("/auth/login", {
        email,
        password,
      });

      const payload = res.data;

      // console.log(payload);

      if (payload?.code === "ACCOUNT_RESTRICTED") {
        dispatch(loginChallenge());

        toast.info(payload?.message);

        return;
      }

      if (payload?.code === "EMAIL_NOT_VERIFIED") {
        dispatch(loginChallenge());

        toast.warning(
          "Email not verified. Check your email for a verification OTP",
        );

        sessionStorage.setItem(
          "otpVerification",
          JSON.stringify({
            email: res.data.email,
            issuedAt: res.data.otp.issuedAt,
            cooldown: res.data.otp.resendCooldownSeconds,
          }),
        );

        navigate("/verify-otp", {
          state: {
            payload,
          },
        });

        return;
      }

      if (payload?.code === "TWO_FA_REQUIRED") {
        dispatch(loginChallenge());
        toast.info(payload.message);

        sessionStorage.setItem(
          "twoFactorOtp",
          JSON.stringify({
            email: payload.email,
            issuedAt: payload.otp.issuedAt,
            cooldown: payload.otp.resendCooldownSeconds,
          }),
        );

        navigate("/two-factor", {
          state: {
            payload,
          },
        });

        return;
      }

      if (payload?.code === "TRANSACTION_PASSWORD_REQUIRED") {
        dispatch(loginSuccess(payload.user));
        dispatch(fetchSettings());

        toast.info("Please create your transaction password to continue.");

        navigate("/setup/transaction-password", {
          replace: true,
          state: {
            required: true,
          },
        });

        return;
      }

      if (payload?.code === "LOGIN_SUCCESS") {
        dispatch(loginSuccess(payload.user));
        dispatch(fetchSettings());

        toast.success("Login successful");

        const role = payload.user.role;

        const target =
          location.state?.from?.pathname ||
          sessionStorage.getItem("redirectAfterLogin") ||
          (role === UserRole.SUPER || role === UserRole.ADMIN
            ? "/admin"
            : "/dashboard");

        sessionStorage.removeItem("redirectAfterLogin");

        navigate(target, {
          replace: true,
        });

        return;
      }
    } catch (err: any) {
      const message = getErrorMessage(err);
      dispatch(loginFailure(message));
      toast.error(message);
      console.log(err);
    }
  };

  return (
    <div className="flex !w-screen !h-full items-center justify-center bg-background p-4 mb-20">
      <div className="w-full sm:max-w-md mx-auto">
        {/* Logo Header */}
        <div className="text-center space-y-3 mb-8">
          <Logo
            iconOnly
            size="xl"
            className="transition-transform duration-300"
          />
          <h1 className="text-2xl font-bold">Welcome to BUYFIAT</h1>
          <p className="text-muted-foreground text-sm">Please sign in</p>
          {/* <p className="text-muted-foreground text-sm">
            Choose how you want to sign in
          </p> */}
        </div>

        {/* Progress Indicator */}
        {/* <div className="flex items-center justify-center gap-2 mb-6">
          <div
            className={cn(
              "h-2 w-8 rounded-full transition-colors",
              currentStep >= 1 ? "bg-primary" : "bg-muted",
            )}
          />
          <div
            className={cn(
              "h-2 w-8 rounded-full transition-colors",
              currentStep >= 2 ? "bg-primary" : "bg-muted",
            )}
          />
        </div> */}

        {/* Main Card */}
        <Card className="border shadow-xl shadow-black/5">
          <CardContent className="p-2 sm:p-4">
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              {/* Social Login Buttons */}
              {/* <div className="space-y-3">
                <Button
                  variant="outline"
                  className="w-full h-14 relative font-medium"
                  onClick={() => handleSocialLogin("google")}
                  disabled={isSocialLoading !== null}
                >
                  {isSocialLoading === "google" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span className="absolute left-4 text-red-500">
                        <GoogleIcon />
                      </span>
                      Continue with Google
                    </>
                  )}
                </Button>

                <Button
                  variant="outline"
                  className="w-full h-14 relative font-medium"
                  onClick={() => handleSocialLogin("apple")}
                  disabled={isSocialLoading !== null}
                >
                  {isSocialLoading === "apple" ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <span className="absolute left-4">
                        <AppleIcon />
                      </span>
                      Continue with Apple
                    </>
                  )}
                </Button>
              </div> */}

              {/* <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <Separator className="w-full" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">
                    Or continue with
                  </span>
                </div>
              </div> */}
            </div>
            {/* Login Form */}
            <form onSubmit={handleRegularLogin} className="space-y-8">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-base">
                  Email Address
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 !h-12"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-base">
                    Password
                  </Label>
                  <Link
                    to="/forgot-password"
                    className="text-primary font-semibold hover:underline"
                  >
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 !h-12"
                    required
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* <div className="flex items-center space-x-2">
                <Checkbox
                  id="remember"
                  checked={rememberMe}
                  onCheckedChange={(checked) =>
                    setRememberMe(checked as boolean)
                  }
                />
                <Label htmlFor="remember" className="text-sm font-normal">
                  Remember me for 30 days
                </Label>
              </div> */}

              <Button
                type="submit"
                className="w-full h-11 disabled:cursor-not-allowed"
                disabled={isLoading || !email.trim() || !password.trim()}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center space-y-4">
          <p className="text-muted-foreground flex items-center gap-4 justify-between">
            <span>Don't have an account?</span>
            <Link
              to="/register"
              className="text-primary font-medium hover:underline"
            >
              Create account
            </Link>
          </p>
          {/* 
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <Link
              to="/terms"
              className="hover:text-foreground transition-colors"
            >
              Terms
            </Link>
            <span>•</span>
            <Link
              to="/privacy"
              className="hover:text-foreground transition-colors"
            >
              Privacy
            </Link>
            <span>•</span>
            <Link
              to="/help"
              className="hover:text-foreground transition-colors"
            >
              Help
            </Link>
          </div> */}

          {/* Security Badge */}
          {/* <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="w-3 h-3 text-emerald-500" />
            <span>Secure 256-bit SSL encrypted</span>
          </div> */}
        </div>
      </div>
    </div>
  );
}
